import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ConfigContext } from '../../../../context';
import { userApi } from '../../../../services';
import {
  Button,
  ButtonType,
  IconLabel,
  InputField,
  emailValidationError,
  getCssVar,
  getOrganisationDomain,
  getWebAppUrl,
  useLoginRedirect,
} from '../../../../shared';
import { FreeLogin } from '../FreeLogin';
import { LoginContentType } from '../Login';
import { SocialLogin } from '../SocialLogin/SocialLogin';
import { TwoFactorAuthSetupProps } from '../TwoFactor';

import classNames from 'classnames';
import formClasses from '../../Form/Form.module.scss';
import classes from './LoginWithEmail.module.scss';

interface LoginWithEmailProps {
  email: string;
  setEmail: Dispatch<SetStateAction<string>>;
  password: string;
  setPassword: Dispatch<SetStateAction<string>>;
  setTwoFactorAuthSetup: Dispatch<SetStateAction<TwoFactorAuthSetupProps>>;
  twoFactorBackHandler: () => void;
  otpProcessing: ({ otp, otpBackup }: { otp?: string; otpBackup?: string }) => void;
  onSuccessLogin: ({ accessToken, webappUrl }: { accessToken: string; webappUrl: string }) => void;
  setLoginContentType: Dispatch<SetStateAction<LoginContentType | null>>;
  isFreeLogin?: boolean;
}

export const LoginWithEmail: FunctionComponent<LoginWithEmailProps> = memo(
  ({
    email,
    setEmail,
    password,
    setPassword,
    setTwoFactorAuthSetup,
    twoFactorBackHandler,
    otpProcessing,
    onSuccessLogin,
    setLoginContentType,
    isFreeLogin,
  }) => {
    const { t } = useTranslation();

    const location = useLocation();

    const referralCode = new URLSearchParams(location.search).get('referralCode');

    const {
      emailPlaceholder,
      login: {
        forgotUrl,
        isRegular,
        social: { google, facebook, apple },
        saml: { enabled: samlEnabled },
        signUpUrl,
      },
    } = useContext(ConfigContext).config.elements;

    const [userLoginByEmail] = userApi.endpoints.userLoginByEmail.useMutation();

    const [userLoginByAfz] = userApi.endpoints.userLoginByAfz.useMutation();

    const { storeLoginRedirect } = useLoginRedirect();

    const [peeping, setPeeping] = useState<boolean>(false);

    const [isEmailValid, setIsEmailValid] = useState<boolean>(true);

    const [credentialError, setCredentialError] = useState<boolean>(false);

    const passwordFieldType = useMemo(() => (peeping ? 'text' : 'password'), [peeping]);

    const forgotPassUrl = useMemo(() => forgotUrl ?? '/auth/forgot?type=magic', [forgotUrl]);

    const loginButton = useRef<HTMLButtonElement>(null);

    const loginActive = useMemo(
      () => email.length && password.length,
      [email.length, password.length]
    );

    const hasSocial = useMemo(
      () => [google, facebook, apple].some((social) => social),
      [apple, facebook, google]
    );

    const renderForgotPass = useMemo(() => {
      if (forgotPassUrl.includes('/auth/forgot')) {
        return (
          <Link
            to={forgotPassUrl}
            className={classNames(formClasses['form__content-link'], classes['forgot'])}
          >
            {t('auth.forgot-password')}
          </Link>
        );
      }

      return (
        <a
          href={forgotPassUrl}
          className={classNames(formClasses['form__content-link'], classes['forgot'])}
          target={'_blank'}
          rel={'noopener noreferrer'}
        >
          {t('auth.forgot-password')}
        </a>
      );
    }, [forgotPassUrl, t]);

    const renderSignup = useMemo(() => {
      if (isRegular) {
        return (
          <div className={classes['sign-up']}>
            <span className={classes['sign-up__label']}>{t('auth.sign-up-label')}</span>
            <Link
              to={{
                pathname: '/auth/signup',
                search: referralCode ? `?referralCode=${referralCode}` : '',
              }}
              className={classNames(formClasses['form__content-link'], classes['sign-up__link'])}
              onClick={storeLoginRedirect}
            >
              {t('auth.sign-up')}
            </Link>
          </div>
        );
      }

      if (!isRegular && !samlEnabled && signUpUrl) {
        return (
          <div className={classes['sign-up']}>
            <span className={classes['sign-up__label']}>{t('auth.sign-up-label-custom')}</span>
            <a
              href={signUpUrl}
              className={classNames(formClasses['form__content-link'], classes['sign-up__link'])}
              target={'_blank'}
              rel={'noopener noreferrer'}
            >
              {t('auth.sign-up-custom')}
            </a>
          </div>
        );
      }

      return null;
    }, [isRegular, referralCode, samlEnabled, signUpUrl, storeLoginRedirect, t]);

    const renderSocial = useMemo(() => {
      if (!isRegular || !hasSocial || isFreeLogin) {
        return null;
      }

      return (
        <>
          <div className={classes['divider']}>{t('auth.or-login-with')}</div>
          <SocialLogin onSuccessLogin={onSuccessLogin} />
        </>
      );
    }, [hasSocial, isFreeLogin, isRegular, onSuccessLogin, t]);

    const emailChangeHandler = useCallback(
      (value: string) => {
        setIsEmailValid(true);
        setCredentialError(false);
        setEmail(value);
      },
      [setEmail]
    );

    const passwordChangeHandler = useCallback(
      (value: string) => {
        setCredentialError(false);
        setPassword(value);
      },
      [setPassword]
    );

    const loginClickHandler = useCallback(async () => {
      setCredentialError(false);

      if (emailValidationError(email)) {
        setIsEmailValid(false);
        return;
      }

      loginButton.current?.setAttribute('disabled', 'true');

      const action = getOrganisationDomain() === 'afz' ? userLoginByAfz : userLoginByEmail;

      const { payload, error } = await action({ email, password }).unwrap();

      if (payload) {
        const { accessToken, channel } = payload;

        await onSuccessLogin({ accessToken, webappUrl: getWebAppUrl(channel.subdomain) });

        return;
      }

      switch (error.__typename) {
        case 'User2FAOtpSetupIsRequiredError': {
          const { otpAuthSecret = '', otpAuthUrlQRCode = '' } = { ...error };
          setTwoFactorAuthSetup({
            otpAuthSecret,
            otpAuthUrlQRCode,
            onVerify: otpProcessing,
            onBack: twoFactorBackHandler,
          });
          setLoginContentType(LoginContentType.SETUP_2FA);
          break;
        }
        case 'User2FAOtpIsRequiredError': {
          setLoginContentType(LoginContentType.CODE_2FA);
          break;
        }
        default: {
          setCredentialError(true);
          loginButton.current?.removeAttribute('disabled');
        }
      }
    }, [
      email,
      onSuccessLogin,
      otpProcessing,
      password,
      setLoginContentType,
      setTwoFactorAuthSetup,
      twoFactorBackHandler,
      userLoginByAfz,
      userLoginByEmail,
    ]);

    return (
      <>
        <InputField
          label={t('common.email')}
          value={email}
          placeholder={emailPlaceholder ?? t('common.email-placeholder')}
          onChange={({ target }) => emailChangeHandler(target.value)}
          error={!isEmailValid}
          {...(!isEmailValid && { errorMessage: t('auth.email-invalid') })}
        />

        <InputField
          label={t('common.password')}
          value={password}
          type={passwordFieldType}
          placeholder={t('common.password-placeholder')}
          icon={
            <IconLabel
              iconId={peeping ? 'eye-crossed' : 'eye'}
              iconSize={18}
              ariaLabel={t(`common.${peeping ? 'hide' : 'show'}-password`)}
              onClick={() => setPeeping(!peeping)}
              color={getCssVar('--input-icon-color')}
              singleColor
            />
          }
          onChange={({ target }) => passwordChangeHandler(target.value)}
        />

        {credentialError && (
          <div className={formClasses['form__content-error']}>{t('auth.login-fail')}</div>
        )}

        {renderForgotPass}

        <Button
          ref={loginButton}
          type={ButtonType.primary}
          label={t('common.login')}
          disabled={!loginActive}
          onClick={loginClickHandler}
          fullWidth
        />

        {!isFreeLogin && !Boolean(referralCode) && <FreeLogin onSuccessLogin={onSuccessLogin} />}

        {isFreeLogin && hasSocial && (
          <Link
            to={'/auth/login'}
            className={classNames(formClasses['form__content-link'])}
            onClick={storeLoginRedirect}
          >
            {t('auth.more-login-options')}
          </Link>
        )}

        {renderSocial}

        {renderSignup}
      </>
    );
  }
);
